/**
 * The loading screen: the logo drawn as SVG so its parts can move on their
 * own. The stamp lands, the viewfinder (brackets and S) blinks, and as it
 * reopens the ring parts on the right and the wordmark slides out of the S.
 * While loading carries on, the viewfinder keeps blinking now and then.
 *
 * The keyframes live in index.css under "Splash".
 */

import { useEffect, useState } from 'react'

// Every screen that shows this is a separate spot in the tree, so going from
// "checking the session" to "loading your plan" remounts it. Timing everything
// from the first mount, and handing the elapsed time to CSS as a negative
// delay, lets the animation carry on rather than start over.
let firstShownAt: number | null = null

// When the wordmark has finished opening out (see --splash-open-at and the
// 700ms animations in index.css), plus a beat so the whole name registers.
const INTRO_MS = 2000
const FADE_MS = 200

/**
 * Keeps the intro on screen until it has played through, even if whatever it
 * covers is ready sooner. The children render (and start loading) underneath
 * straight away; this only decides when they become visible. If they're still
 * loading when the intro ends, their own Splash carries on in step, so the
 * handover can't be seen.
 */
export function SplashHold({ children }: { children: React.ReactNode }) {
  const [stage, setStage] = useState<'intro' | 'fading' | 'done'>(() =>
    // Reduced motion jumps straight to the finished lockup, so there's
    // nothing to wait for.
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'done' : 'intro',
  )

  useEffect(() => {
    if (stage === 'done') return
    firstShownAt ??= performance.now()
    const remaining = Math.max(0, firstShownAt + INTRO_MS - performance.now())
    const fade = setTimeout(() => setStage('fading'), remaining)
    const done = setTimeout(() => setStage('done'), remaining + FADE_MS)
    return () => {
      clearTimeout(fade)
      clearTimeout(done)
    }
    // Timed once, from the first mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {children}
      {stage !== 'done' && (
        <div
          // The screen underneath announces its own loading state.
          aria-hidden
          className="fixed inset-0 z-50 bg-paper transition-opacity"
          style={{ opacity: stage === 'fading' ? 0 : 1, transitionDuration: `${FADE_MS}ms` }}
        >
          <Splash />
        </div>
      )}
    </>
  )
}

export default function Splash({ label = 'Loading…' }: { label?: string }) {
  firstShownAt ??= performance.now()
  const elapsed = Math.round(performance.now() - firstShownAt)

  return (
    <div
      role="status"
      className="splash min-h-[100dvh] flex items-center justify-center px-4 text-accent"
      style={{ '--splash-elapsed': `-${elapsed}ms` } as React.CSSProperties}
    >
      <span className="sr-only">{label}</span>
      <svg viewBox="0 0 418 120" className="w-full max-w-[520px] overflow-visible" aria-hidden>
        {/* Starts shifted right so the lone coin sits in the middle, then
            slides back as the wordmark opens out. */}
        <g className="splash-lockup">
          <g className="splash-stamp">
            <circle
              className="splash-ring"
              cx="60"
              cy="60"
              r="54"
              fill="none"
              stroke="currentColor"
              strokeWidth="6"
              pathLength={360}
            />
            <g className="splash-eye">
              <path
                d="M30 41V30h11 M79 30h11v11 M30 79v11h11 M90 79v11H79"
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="square"
              />
              <text
                x="60"
                y="60"
                dy="0.35em"
                textAnchor="middle"
                fill="currentColor"
                className="font-sans"
                fontSize="64"
                fontWeight="700"
              >
                S
              </text>
            </g>
          </g>
          <g clipPath="url(#splash-reveal)">
            <text
              className="splash-word font-sans"
              x="96"
              y="82"
              fill="currentColor"
              fontSize="38"
              letterSpacing="0.5"
            >
              <tspan fontWeight="600">NAPSHOT</tspan>
              <tspan fontWeight="400"> MONEY</tspan>
            </text>
          </g>
        </g>
        <defs>
          {/* Hides anything left of the S's right edge, so the words look
              like they come out from behind it. */}
          <clipPath id="splash-reveal">
            <rect x="84" y="0" width="400" height="120" />
          </clipPath>
        </defs>
      </svg>
    </div>
  )
}
